import { Select } from '@deque/cauldron-react';
import type { TFunction } from 'i18next';
import React from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type UseFormGetValues
} from 'react-hook-form';

import { JiraFieldMappersKeys } from '@deque/ui-export-jira';
import { ProductNames } from '~/common/constants';
import type { SelectOption } from '../FieldMapping';
import styles from './Field.css';

import { LabelsFieldAssignment } from './LabelFieldAssignment';
import { SummaryPrefixInput } from './SummaryPrefixInput';

export interface FieldAssignmentProps {
  axeFields: SelectOption[];
  mappingFields: SelectOption[];
  index: number;
  control: Control<FieldValues, unknown>;
  getError: (
    field: {
      value: string;
    },
    fieldError: FieldErrors<FieldValues>,
    i: number
  ) => string | undefined;
  errors: FieldErrors<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  t: TFunction<'translation', undefined>;
  integrationProductLabels: string[];
}

const FieldAssignment = ({
  axeFields,
  mappingFields,
  index,
  control,
  getError,
  errors,
  getValues,
  t,
  integrationProductLabels
}: FieldAssignmentProps) => {
  const mappingField = mappingFields.find(
    mappingFieldToCheck =>
      mappingFieldToCheck.key ===
      getValues().fieldMapping?.[index]?.mappingField // ? are needed for unit tests to pass, not needed in real usage
  );
  /* istanbul ignore next */
  const isEnum: boolean = mappingField?.fieldWithMeta?.isEnum === true;
  const isLabelField: boolean =
    mappingField?.fieldWithMeta?.mapper?.id === JiraFieldMappersKeys.Labels;
  const isSummaryField: boolean =
    mappingField?.fieldWithMeta?.mapper?.id ===
      JiraFieldMappersKeys.ShortText &&
    mappingField?.fieldWithMeta?.field.fieldId === 'summary';

  let spacer = <></>;
  let controller = <></>;

  if (mappingField && (isEnum || isLabelField)) {
    spacer = (
      <p className={styles.from} id={`from-${index}`}>
        {t(`set to`)}
      </p>
    );

    let enumValues: SelectOption[] | undefined =
      mappingField.fieldWithMeta?.field.allowedValues?.map(allowedValue => {
        const { value, name, id } = allowedValue;

        return {
          key: id,
          label: value || name,
          value: id
        };
      });
    if (enumValues) {
      enumValues = [
        {
          key: '',
          label: '',
          value: ''
        },
        ...enumValues
      ];
    }

    controller = (
      <Controller
        name={`fieldMapping.${index}.axeField`}
        control={control}
        render={({ field }) =>
          isLabelField ? (
            <LabelsFieldAssignment
              index={index}
              axeFields={axeFields}
              integrationProductLabels={integrationProductLabels}
            />
          ) : (
            <Select
              {...field}
              label={t(`Field value`)}
              options={enumValues}
              error={getError(field, errors, index)}
              id={`axe-field-${index}`}
              aria-label={t(`{{ axe }} field {{ index }}`, {
                axe: ProductNames.axe,
                index: index + 1
              })}
            />
          )
        }
      />
    );
  } else {
    spacer = (
      <p className={styles.from} id={`from-${index}`}>
        {t(`from`)}
      </p>
    );

    controller = (
      <Controller
        name={`fieldMapping.${index}.axeField`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            label={t(`{{ axe }} Field`, {
              axe: ProductNames.axe
            })}
            options={axeFields}
            error={getError(field, errors, index)}
            id={`axe-field-${index}`}
            aria-label={t(`{{ axe }} field {{ index }}`, {
              axe: ProductNames.axe,
              index: index + 1
            })}
          />
        )}
      />
    );
  }

  return (
    <>
      {spacer}
      {controller}
      {isSummaryField && <SummaryPrefixInput />}
    </>
  );
};

export default FieldAssignment;
